<script setup lang="ts">
import { getInitialsFromTrainer } from '@/helpers/intials.helper';
import { Kenmerk } from '@/repositories/kenmerk.repository';
import { TrainerSearchItem } from '@/repositories/trainer.repository';
import { usePlatformStore } from '@/stores/platform.store';
import { Tooltip } from 'floating-vue';
import { computed } from 'vue';


const MAX_VISIBLE_TAGS = 1;
const platformStore = usePlatformStore();

const props = defineProps<{
    entity: TrainerSearchItem,
    kenmerken: Kenmerk[],
    trainerSingular: string,
    trainerPlural: string,
}>();

const emits = defineEmits<{
    (e: 'selected', entity: TrainerSearchItem): void
}>();

const distanceInKm = computed(() => {
    const kilometers = props.entity.LocatieItem.Afstand / 1000;
    return +(Math.round(Number(kilometers + "e+2")) + "e-2"); // Round two decimals
});

const visibleTags = computed(() => {
    return trainerKenmerken.value.slice(0, MAX_VISIBLE_TAGS);
});

const hiddenTags = computed(() => {
    return trainerKenmerken.value.slice(MAX_VISIBLE_TAGS);
});

const hasHiddenTags = computed(() => {
    return hiddenTags.value.length > 0;
});

const trainerKenmerken = computed(() => {
    if (!props.entity.Opleidingen?.length) return [];

    return props.entity.Opleidingen
        .map(id => props.kenmerken.find(k => k.Id === id))
        .filter((k): k is Kenmerk => !!k);
});

const woonplaats = computed(() => {
    return [props.entity.LocatieItem?.Adres, props.entity.LocatieItem?.Woonplaats]
        .filter(v => v && v.trim() !== "")
        .join(", ");
})


const selectTrainer = () => {
    emits('selected', props.entity);
}
</script>

<template>
    <div class="trainer-card">
        <h2 class="h5 trainer-card__title trainer-card__title--mobile"  role="button" @click="selectTrainer">{{ entity.Voornaam }} {{ entity.Achternaam }}
        </h2>

        <div class="trainer-card__body">
            <img v-if="entity.AfbeeldingUrl" :src="entity.AfbeeldingUrl + '?width=300&height=300&mode=crop'" class="trainer-card__image" role="button" @click="selectTrainer"/>
            <div v-else class="trainer-card__image trainer-card__image--placeholder">
                {{ getInitialsFromTrainer(entity) }}
            </div>

            <div class="trainer-card__information">
                <div class="trainer-card__title-row">
                    <h2 class="h5 trainer-card__title trainer-card__title--desktop" role="button" @click="selectTrainer">
                        {{ entity.Voornaam }} {{ entity.Achternaam }}
                    </h2>

                    <div class="trainer-card__tag-wrapper">
                        <span v-for="tag in visibleTags" :key="tag.Id" class="trainer-card__tag">
                            <span class="icon icon-editor-choice"></span>
                            <span>{{ tag.Naam }}</span>
                        </span>

                        <Tooltip v-if="hasHiddenTags" placement="top" class="trainer-card__tag more-tags" :triggers="['click', 'hover']">
                            <span>
                                +{{ hiddenTags.length }}
                            </span>

                            <template #popper>
                                <div class="trainer-tags-tooltip">
                                    <div v-for="tag in hiddenTags" :key="tag.Id" class="trainer-tags-tooltip__item">
                                        <span class="icon icon-editor-choice"></span>
                                        <span>{{ tag.Naam }}</span>
                                    </div>
                                </div>
                            </template>
                        </Tooltip>
                    </div>
                </div>

                <div class="trainer-card__location">
                    <div class="trainer-card__location-club">
                        <span class="icon icon-sports-golf" v-if="platformStore.isGolfPlatform"></span>
                        <span>{{ entity.SportaanbiederItem.Naam }}</span>
                    </div>
                    <div class="trainer-card__location-address">
                        <span class="icon icon-map-pin"></span>
                        <span>{{ woonplaats }}</span>
                        <span v-if="entity.LocatieItem.Afstand" class="trainer-card__location-address-distance">(± {{
                            distanceInKm
                            }}km)</span>
                    </div>
                </div>

                <div class="trainer-card__description trainer-card__description--desktop">
                    {{ entity.Informatie }}
                </div>
            </div>
        </div>

        <div class="trainer-card__divider"></div>

        <div class="trainer-card__description trainer-card__description--mobile">
            {{ entity.Informatie }}
        </div>

        <div class="trainer-card__aside">
            <button class="btn btn-sm btn-secondary" @click="selectTrainer">Bekijk {{ trainerSingular }}</button>
        </div>
    </div>
</template>